import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, Observable, switchMap, tap } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { StorageKeys, StorageService } from '../../core/services/storage/storage.service';
import { OrderResponse } from '../../interfaces/orders';
import { Sim } from '../../interfaces/sims';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { distinctAuthenticatedState } from '../../utils/auth-distinct.pipe';

@Injectable({
  providedIn: 'root',
})
export class SimsService {
  public sims = signal<Sim[]>([]);

  protected apiConfig = inject(ApiConfigService);

  protected http = inject(HttpClient);

  protected authService = inject(AuthService);

  protected isAuthenticated = toObservable(this.authService.isAuthenticated);

  protected $document = inject(DOCUMENT);

  protected storageService = inject(StorageService);

  public singleEsim = signal<Sim | null>(null);

  public orderDetails = signal<OrderResponse | null>(null);

  constructor() {
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();

      if (!isAuthenticated) {
        this.sims.set([]);
      }
    });
  }

  public initAutoLoadSims(): void {
    distinctAuthenticatedState(this.isAuthenticated)
      .pipe(tap({ next: () => this.initEsimsFromStorage() }))
      .pipe(switchMap(() => this.fetchSims()))
      .subscribe();
  }

  public fetchSims(): Observable<Sim[]> {
    return this.http.get<Sim[]>(this.apiConfig.url('esims')).pipe(
      map((result) => {
        this.sims.set(result);
        this.storageService.setKey(StorageKeys.ESims, result);
        return result;
      })
    );
  }

  /**
   * Updates the esim name
   * @param esimId
   * @param name
   * @returns
   */
  public updateName(esimId: string, name: string): Observable<{ message: string }> {
    return this.http
      .patch<{ message: string }>(this.apiConfig.url(`esims/${esimId}`), { name })
      .pipe(
        switchMap((result) => {
          return this.fetchSims().pipe(map(() => result));
        })
      );
  }

  /**
   * Returns the pdf content as base64 encoded string
   * @param subscriptionId
   * @param transactionId
   * @returns
   */
  public downloadInvoice(
    subscriptionId: string,
    invoiceName = 'invoice.pdf',
    hash?: string
  ): Observable<true> {
    return this.http
      .get(this.apiConfig.url(`subscriptions/${subscriptionId}/download-invoice`), {
        params: hash ? { hash } : {},
        responseType: 'arraybuffer',
      })
      .pipe(
        map((arrayBuffer) => {
          let binary = '';
          const bytes = new Uint8Array(arrayBuffer);
          const len = bytes.byteLength;

          for (let i = 0; i < len; i++) {
            binary += String.fromCodePoint(bytes[i]);
          }
          const pdfContent = btoa(binary); // encode to base64
          const linkSource = `data:application/pdf;base64,${pdfContent}`;
          const downloadLink = this.$document.createElement('a');
          const fileName = invoiceName;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();

          return true;
        })
      );
  }

  /**
   * Initializes the esims from storage
   */
  public initEsimsFromStorage(): void {
    const esims = this.storageService.getKey<Sim[]>(StorageKeys.ESims);

    if (esims) {
      this.sims.set(esims);
    }
  }

  public fetchSingleEsim(esimId: string): Observable<Sim> {
    return this.http.get<Sim>(this.apiConfig.url(`esims/${esimId}`)).pipe(
      tap((result) => {
        this.singleEsim.set(result);
      })
    );
  }

  public getOrderDetails(): Observable<OrderResponse[]> {
    return this.http.get<OrderResponse[]>(this.apiConfig.url('orders'));
  }

  public getOrderDetailsByOrderId(orderId: string): Observable<OrderResponse> {
    return this.http.get<OrderResponse>(this.apiConfig.url(`orders/${orderId}`)).pipe(
      tap((result) => {
        this.orderDetails.set(result);
      })
    );
  }
}
